// src/public/PublicEstimatePage.js
// Customer-facing estimate page — no auth, no sidebar, no tenant header
// Token read from URL: /public/estimate/:token
// Business + branding returned directly from GET /public/estimate/:token
// Item fields: item.qty, item.rate, item.description — matches DB items_json
// Zero JOBFLOW NOW branding — subscriber identity only

import React, { useState, useEffect, useRef } from 'react';
import { getApiBase } from '../apiBase';
import InvoicePrint from '../invoiceprint';

const API_BASE = getApiBase();

const PublicEstimatePage = () => {
  const [estimate, setEstimate]     = useState(null);
  const [business, setBusiness]     = useState(null);
  const [branding, setBranding]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [actionDone, setActionDone] = useState(false);
  const [actionMsg, setActionMsg]   = useState('');
  const [error, setError]           = useState('');
  const viewTrackingStartedRef      = useRef(false);

  const token = window.location.pathname.split('/').pop();

  useEffect(() => {
    if (!token) { setError('Invalid link — no token found.'); setLoading(false); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!estimate || !token || viewTrackingStartedRef.current) return undefined;

    const trackIfVisible = () => {
      if (document.visibilityState !== 'visible' || viewTrackingStartedRef.current) return;

      viewTrackingStartedRef.current = true;
      document.removeEventListener('visibilitychange', trackIfVisible);

      fetch(`${API_BASE}/public/estimate/${token}/view`, {
        method: 'POST',
        keepalive: true
      }).catch(() => undefined);
    };

    trackIfVisible();

    if (!viewTrackingStartedRef.current) {
      document.addEventListener('visibilitychange', trackIfVisible);
    }

    return () => {
      document.removeEventListener('visibilitychange', trackIfVisible);
    };
  }, [estimate, token]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const estRes = await fetch(`${API_BASE}/public/estimate/${token}`);
      if (estRes.status === 404) { setError('This estimate link was not found.'); return; }
      if (estRes.status === 410) { setError('This link has expired or been revoked.'); return; }
      if (!estRes.ok) { setError('Failed to load estimate.'); return; }
      const estData = await estRes.json();
      setEstimate(estData.estimate);
      setBusiness(estData.business || null);
      setBranding(estData.branding || null);
    } catch (e) {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      const res = await fetch(`${API_BASE}/public/estimate/${token}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.status === 409) { setActionMsg(data.error || 'This estimate has already been actioned.'); setActionDone(true); return; }
      if (!res.ok) { setActionMsg(data.error || 'Something went wrong. Please try again.'); return; }
      const newStatus = action === 'approve' ? 'approved' : 'declined';
      setEstimate(prev => ({ ...prev, status: newStatus }));
      setActionDone(true);
      setActionMsg(
        action === 'approve'
          ? '✅ ESTIMATE APPROVED — Thank you! We will be in touch shortly.'
          : '❌ ESTIMATE DECLINED — Thank you for letting us know.'
      );
    } catch (e) {
      setActionMsg('Network error — please try again.');
    }
  };

  const pageStyle = { minHeight: '100vh', backgroundColor: '#e9eef5', padding: 'clamp(10px, 4vw, 40px)', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' };
  const cardStyle = { maxWidth: '800px', margin: '0 auto' };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', padding: '60px', backgroundColor: '#fff', border: '4px solid black' }}>
          <div style={{ fontWeight: 900, fontSize: '18px' }}>⏳ LOADING ESTIMATE...</div>
        </div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '30px', textAlign: 'center', fontWeight: 900, fontSize: '16px', border: '4px solid black', backgroundColor: '#fee2e2' }}>
            ⚠️ {error || 'Estimate not found.'}
          </div>
        </div>
      </div>
    );
  }

  const status    = String(estimate.status || 'draft').toLowerCase();
  const canAction = !actionDone && status !== 'approved' && status !== 'declined' && status !== 'converted';

  const statusColors = {
    draft:     { bg: '#f3f4f6', color: '#374151' },
    sent:      { bg: '#dbeafe', color: '#1e40af' },
    approved:  { bg: '#dcfce7', color: '#166534' },
    declined:  { bg: '#fee2e2', color: '#991b1b' },
    converted: { bg: '#fef9c3', color: '#854d0e' }
  };
  const statusColor = statusColors[status] || statusColors.draft;

  // Inject branding and business into the estimate object so InvoicePrint can
  // use them without hitting the authenticated internal API endpoints.
  // __isBrandingPreview: true → InvoicePrint reads inv.branding instead of fetching.
  // __business* fields     → InvoicePrint reads these in the preview business path.
  // invoice_number         → InvoicePrint reads inv.invoice_number for the doc number.
  const mergedEstimate = {
    ...estimate,
    invoice_number: estimate.estimate_number,
    __isBrandingPreview: true,
    branding,
    __businessName:    business?.businessName || '',
    __businessAddress: business?.address      || '',
    __businessCity:    business?.city         || '',
    __businessState:   business?.state        || '',
    __businessZip:     business?.zip          || '',
    __businessPhone:   business?.phone || business?.mobile || '',
    __businessEmail:   business?.email        || '',
    __businessWebsite: business?.website      || '',
    __publicBusinessSettings: business || {},
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>

        <InvoicePrint invoice={mergedEstimate} docType="ESTIMATE" />

        {/* Approve / Decline Buttons */}
        {canAction && (
          <div style={{ display: 'flex', gap: '16px', marginTop: '24px', flexWrap: 'wrap' }}>
            <button
              style={{ flex: 1, padding: '18px', backgroundColor: '#39FF14', color: 'black', border: '4px solid black', fontWeight: 900, fontSize: '18px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' }}
              onClick={() => handleAction('approve')}
            >
              ✅ APPROVE ESTIMATE
            </button>
            <button
              style={{ flex: 1, padding: '18px', backgroundColor: '#ff4444', color: 'white', border: '4px solid black', fontWeight: 900, fontSize: '18px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' }}
              onClick={() => handleAction('decline')}
            >
              ✗ DECLINE
            </button>
          </div>
        )}

        {/* Action confirmation message */}
        {actionMsg && (
          <div style={{
            padding: '20px', border: '4px solid black', fontWeight: 900, fontSize: '16px',
            textAlign: 'center', marginTop: '20px',
            backgroundColor: estimate.status === 'approved' ? '#dcfce7' : estimate.status === 'declined' ? '#fee2e2' : 'white'
          }}>
            {actionMsg}
          </div>
        )}

        {/* Already actioned — no buttons */}
        {!canAction && !actionMsg && (
          <div style={{ padding: '20px', border: '4px solid black', fontWeight: 900, fontSize: '16px', textAlign: 'center', marginTop: '20px', backgroundColor: statusColor.bg, color: statusColor.color }}>
            This estimate has been <strong>{status.toUpperCase()}</strong>.
          </div>
        )}

      </div>
    </div>
  );
};

export default PublicEstimatePage;
